import { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { Button, Input, Modal, Popconfirm, Space, Table, TablePaginationConfig, message } from "antd";
import { ColumnType } from "antd/es/table";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";

import StringUtils from "../../util/StringUtils";
import TenantApi from "../../api/TenantApi";
import { FilterItem, FilterItemLabel, TableWrapper, TenantManagementWrapper, ToolBarLeftWrapper, ToolBarRightWrapper, ToolBarWrapper } from "./TenantManagement.styled";
import CreateTenantWizard from "./components/create-tenant-wizard/CreateTenantWizard";
import TenantDetails from "./components/tenant-details/TenantDetails";

interface TenantManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
}

const TenantManagement: React.FC<TenantManagementProps> = (props)=> {
    // api
    let tenantApi = new TenantApi();
    const [messageApi, contextHolder] = message.useMessage();
    let navigate = useNavigate();

    // state
    let [tenantViews, setTenantViews] = useState([]);
    let [openCreateTenantDialog, setOpenCreateTenantDialog] = useState(false);

    let [loading, setLoading] = useState(true);
    let [search, setSearch] = useState("");
    let [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    // events
    let viewTenantDetails = (tenantView: any)=> {
        navigate(`./tenant/${tenantView.tenant.id}`);
    };

    let onSearchChange = (e)=> {
        let search = e.target.value;
        setSearch(search);
        loadTenantViews();
    }

    let onTableChange = (pagination)=> {
        tableParams.pagination = pagination;
        setTableParams({pagination: pagination});
        loadTenantViews();
    };

    let onCreateTenantClick = ()=> {
        setOpenCreateTenantDialog(true);
    };

    // load device views
    let loadTenantViews = async ()=> {
        // loading animation
        setLoading(true);
        let deviceViewsPage = (await tenantApi.queryViews({
            search: search,
            pageNo: tableParams.pagination.current,
            pageSize: tableParams.pagination.pageSize,
        })).data.data;

        let newTableParams = {pagination: {...tableParams.pagination}};
        newTableParams.pagination.total = deviceViewsPage.totalElements;
        setTableParams(newTableParams);
        setTenantViews(deviceViewsPage.elements);
        setLoading(false);
    };

    let getTenantStatusText = (tenantView)=> {
        switch(tenantView.tenant.status) {
            case 'ACTIVE': 
                return '正常';
            case 'DISABLED':
                return '禁用';
            case 'EXPIRED':
                return '过期';
            default:
                return '未知';
        }
    };

    // column definition
    let columns: ColumnType<any>[] = [
        {
            title: '公司名称',
            dataIndex: 'tenant.name',
            key: 'tenant.name',
            align: 'center',
            render: (text, record, index)=> {
                return <a onClick={()=> viewTenantDetails(record)}>{record.tenant.name}</a>
            }
        },
        {
            title: '租户编码',
            dataIndex: 'tenant.tenantNo',
            key: 'tenant.tenantNo',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.tenant.tenantNo)}</span>
            }
        },
        {
            title: '行业',
            dataIndex: 'tenant.industry',
            key: 'tenant.industry',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.tenant.industry)}</span>
            }
        },
        {
            title: '联系人',
            dataIndex: 'tenant.contactName',
            key: 'tenant.contactName',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ StringUtils.emptyToSlash(record.tenant.contactName) }</span>
            }
        },
        {
            title: '状态',
            dataIndex: 'user.disabled',
            key: "user.disabled",
            align: 'center',
            render: (text, record, index)=> {
                return getTenantStatusText(record);
            }
        },
        {
            title: '创建时间',
            dataIndex: 'tenant.createdTime',
            key: 'tenant.createdTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{ moment(parseInt(record.tenant.createdTime)).format("YYYY-MM-DD HH:mm:ss") }</span>
            }
        },
        {
            title: '备注',
            dataIndex: 'tenant.remarks',
            key: 'tenant.remarks',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{StringUtils.emptyToSlash(record.tenant.remarks)}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> viewTenantDetails(record)}>查看详情</a>
                </Space>
            }
        }
    ];

    // effect
    useEffect(()=> {
        loadTenantViews();
    }, []);

    // render
    return (
        <TenantManagementWrapper>
            {contextHolder}
            <ToolBarWrapper>
                <ToolBarLeftWrapper>
                    <Space size="middle">
                        <Button type="primary" size="middle" icon={<PlusOutlined />} onClick={onCreateTenantClick}>创建租户</Button>
                    </Space>
                </ToolBarLeftWrapper>

                <ToolBarRightWrapper>
                    <FilterItem>
                        <Input 
                            placeholder="请输入关键字" 
                            suffix={<SearchOutlined />} 
                            size="middle" 
                            value={search} 
                            onChange={(e)=> onSearchChange} 
                        />
                    </FilterItem>
                </ToolBarRightWrapper>

                <Modal open={openCreateTenantDialog} 
                    onClose={()=> setOpenCreateTenantDialog(false)} 
                    footer={null} 
                    closable={false}
                    destroyOnClose={true}
                    width={600}
                    height={800}
                    >
                    <CreateTenantWizard onClose={()=> setOpenCreateTenantDialog(false)} onComplete={()=> loadTenantViews()} />
                </Modal>
            </ToolBarWrapper>
            <TableWrapper>
                <Table
                    rowKey={(record)=> record.tenant.id}
                    columns={columns} 
                    dataSource={tenantViews}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={onTableChange}
                />
            </TableWrapper>

            <Routes>
                <Route path="tenant/:tenantId" element={<TenantDetails onClose={()=>{}} onComplete={()=>{}}/>} />
            </Routes>
        </TenantManagementWrapper>
    );
}

export default TenantManagement;
